import type * as React from 'react'
import { useId, useRef, useState } from 'react'
import type { JSX } from 'react'

import { Dialog, toast, VisuallyHidden } from '@buffer-mono/popcorn'

import { useUpdateContentItemTitle } from '../../hooks/useUpdateContentItemTitle'
import {
  useOmniDraftId,
  useOmniTitle,
  useSetOmniTitle,
} from '../../store/postSlice/hooks'

import styles from './ContentItemTitle.module.css'

const TITLE_MAX_LENGTH = 200

/**
 * Inline-editable `ContentItem.title`, styled to read as plain header text
 * until focused (Notion/Linear-style in-place editing) rather than a visible
 * form field. Works before the first save: with no `draftId` yet, a typed
 * title is held in composer state and rides along on the next save; once a
 * `draftId` exists, edits write through immediately.
 */
export const ContentItemTitle = ({
  fallback,
}: {
  fallback: string
}): JSX.Element => {
  const title = useOmniTitle()
  const setOmniTitle = useSetOmniTitle()
  const draftId = useOmniDraftId()
  const updateContentItemTitle = useUpdateContentItemTitle()

  const [value, setValue] = useState(title ?? '')
  const cancelledRef = useRef(false)
  const descriptionId = useId()
  const latestRequestRef = useRef(0)

  const handleSubmit = async (newTitle: string): Promise<void> => {
    if (newTitle === (title ?? '')) return

    setOmniTitle(newTitle || null)

    if (draftId) {
      const requestId = ++latestRequestRef.current
      const result = await updateContentItemTitle(draftId, newTitle || null)
      // A newer edit already superseded this one — its own resolution owns
      // the title now, so don't roll back over it with this stale result.
      if (requestId !== latestRequestRef.current) return
      if (!result.success) {
        setOmniTitle(title)
        setValue(title ?? '')
        toast.error("Couldn't save the title. Please try again.")
      }
    }
  }

  const handleBlur = (): void => {
    if (cancelledRef.current) {
      cancelledRef.current = false
      return
    }
    void handleSubmit(value.trim())
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event,
  ): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.currentTarget.blur()
    } else if (event.key === 'Escape') {
      cancelledRef.current = true
      setValue(title ?? '')
      event.currentTarget.blur()
    }
  }

  return (
    <div className={styles.container}>
      {/* Dialog's aria-labelledby target: mirrors the input's committed
          value so it stays accurate without updating on every keystroke. */}
      <VisuallyHidden>
        <Dialog.Title>{title || fallback}</Dialog.Title>
      </VisuallyHidden>
      <input
        className={styles.input}
        aria-label="Post title"
        aria-describedby={descriptionId}
        maxLength={TITLE_MAX_LENGTH}
        placeholder={fallback}
        size={Math.max((value || fallback).length, 1)}
        value={value}
        onChange={(e): void => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      <VisuallyHidden id={descriptionId}>
        Press <kbd>Enter</kbd> to save or <kbd>Esc</kbd> to cancel
      </VisuallyHidden>
      <VisuallyHidden>
        <Dialog.Description>Create and edit posts</Dialog.Description>
      </VisuallyHidden>
    </div>
  )
}
